const ACTIVE = new Set(['scheduled', 'confirmed', 'en_route', 'in_progress']);
const NOT_STARTED = new Set(['scheduled', 'confirmed']);
function minutesBetween(start, end) {
    return Math.max(0, Math.round((new Date(end).getTime() - new Date(start).getTime()) / 60_000));
}
function unique(values) {
    return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}
function skillKey(value) {
    return value
        .toLowerCase()
        .trim()
        .replace(/[^a-z0-9]+/g, '_')
        .replace(/^_+|_+$/g, '');
}
function localParts(value, timezone) {
    const parts = new Intl.DateTimeFormat('en-CA', {
        timeZone: timezone,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hourCycle: 'h23',
    })
        .formatToParts(new Date(value))
        .reduce((result, part) => {
        if (part.type !== 'literal')
            result[part.type] = part.value;
        return result;
    }, {});
    return {
        date: `${parts.year}-${parts.month}-${parts.day}`,
        minutes: Number(parts.hour) * 60 + Number(parts.minute),
    };
}
function timeMinutes(value) {
    if (!value)
        return null;
    const [hour, minute] = value.split(':').map(Number);
    return Number.isFinite(hour) && Number.isFinite(minute) ? hour * 60 + minute : null;
}
function overlaps(left, right, bufferMinutes = 0) {
    const buffer = bufferMinutes * 60_000;
    return (new Date(left.starts_at).getTime() < new Date(right.ends_at).getTime() + buffer &&
        new Date(left.ends_at).getTime() + buffer > new Date(right.starts_at).getTime());
}
function severityFor(appointment) {
    return appointment.priority === 'urgent' ? 'urgent' : 'warning';
}
/**
 * Add progressive dispatch intelligence to the tenant-scoped database snapshot.
 * The same deterministic function is used by the browser and MCP server so
 * recommendations and reveal conditions cannot drift between interfaces.
 */
export function deriveDispatchBoard(raw) {
    const preferences = raw.preferences ?? {
        late_grace_minutes: 15,
        workday_start: null,
        workday_end: null,
        travel_buffer_minutes: null,
    };
    const requirements = raw.service_requirements.filter((item) => item.enabled);
    const requirementsByService = new Map(requirements.map((item) => [item.service_id, item]));
    const requiredSkills = {};
    const expectedMinutes = new Map();
    for (const appointment of raw.appointments) {
        const matching = appointment.service_ids
            .map((id) => requirementsByService.get(id))
            .filter((value) => Boolean(value));
        requiredSkills[appointment.id] = unique(matching.flatMap((item) => item.required_skills.map(skillKey)));
        const expected = matching.reduce((total, item) => total + (item.expected_minutes ?? 0), 0);
        if (expected > 0)
            expectedMinutes.set(appointment.id, expected);
    }
    const reveal = {
        attention: false,
        team_capacity: raw.staff.length > 1,
        skill_matching: requirements.some((item) => item.required_skills.length > 0),
        working_hours: Boolean(preferences.workday_start && preferences.workday_end),
        travel_buffer: (preferences.travel_buffer_minutes ?? 0) > 0,
        duration_guidance: requirements.some((item) => (item.expected_minutes ?? 0) > 0),
    };
    const active = raw.appointments.filter((appointment) => ACTIVE.has(appointment.status));
    const staffById = new Map(raw.staff.map((staff) => [staff.id, staff]));
    const attention = [];
    const recommendations = [];
    const workload = new Map(raw.staff.map((staff) => [staff.id, 0]));
    for (const appointment of active) {
        for (const assignee of appointment.staff)
            workload.set(assignee.id, (workload.get(assignee.id) ?? 0) +
                minutesBetween(appointment.starts_at, appointment.ends_at));
    }
    function availableCandidate(appointment) {
        const needed = requiredSkills[appointment.id] ?? [];
        return [...raw.staff]
            .filter((candidate) => {
            const candidateSkills = candidate.skills.map(skillKey);
            return needed.every((skill) => candidateSkills.includes(skill));
        })
            .filter((candidate) => active
            .filter((other) => other.id !== appointment.id && other.staff.some((row) => row.id === candidate.id))
            .every((other) => !overlaps(appointment, other, preferences.travel_buffer_minutes ?? 0)))
            .sort((left, right) => (workload.get(left.id) ?? 0) - (workload.get(right.id) ?? 0))[0];
    }
    if (raw.staff.length > 0) {
        for (const appointment of active.filter((item) => item.staff.length === 0)) {
            const candidate = availableCandidate(appointment);
            const recommendation = candidate
                ? {
                    kind: 'assign_staff',
                    appointment_id: appointment.id,
                    staff_id: candidate.id,
                    staff_name: candidate.name,
                    reason: `Available${requiredSkills[appointment.id]?.length ? ' and has the required skills' : ''}.`,
                    tool: 'update_appointment',
                    args: { id: appointment.id, staff_ids: [candidate.id] },
                }
                : undefined;
            if (recommendation)
                recommendations.push(recommendation);
            attention.push({
                key: `unassigned:${appointment.id}`,
                kind: 'unassigned',
                severity: severityFor(appointment),
                title: `${appointment.job_number} is unassigned`,
                detail: candidate
                    ? `${candidate.name} is the lowest-load available match.`
                    : 'No available worker matches this time and its configured requirements.',
                appointment_ids: [appointment.id],
                staff_ids: [],
                recommendation,
            });
        }
    }
    const now = new Date(raw.generated_at);
    const today = localParts(raw.generated_at, raw.timezone).date;
    for (const appointment of active.filter((item) => NOT_STARTED.has(item.status))) {
        const local = localParts(appointment.starts_at, raw.timezone);
        if (local.date === today &&
            new Date(appointment.starts_at).getTime() <
                now.getTime() - preferences.late_grace_minutes * 60_000) {
            attention.push({
                key: `late:${appointment.id}`,
                kind: 'late',
                severity: severityFor(appointment),
                title: `${appointment.job_number} has not started`,
                detail: `Its start time passed more than ${preferences.late_grace_minutes} minutes ago.`,
                appointment_ids: [appointment.id],
                staff_ids: appointment.staff.map((item) => item.id),
            });
        }
    }
    for (let index = 0; index < active.length; index += 1) {
        for (let nextIndex = index + 1; nextIndex < active.length; nextIndex += 1) {
            const left = active[index];
            const right = active[nextIndex];
            const shared = left.staff
                .map((item) => item.id)
                .filter((id) => right.staff.some((item) => item.id === id));
            if (!shared.length)
                continue;
            if (overlaps(left, right)) {
                attention.push({
                    key: `conflict:${left.id}:${right.id}`,
                    kind: 'conflict',
                    severity: left.priority === 'urgent' || right.priority === 'urgent' ? 'urgent' : 'warning',
                    title: `${left.job_number} conflicts with ${right.job_number}`,
                    detail: `${shared.map((id) => staffById.get(id)?.name ?? 'A worker').join(', ')} is assigned to overlapping visits.`,
                    appointment_ids: [left.id, right.id],
                    staff_ids: shared,
                });
            }
            else if (reveal.travel_buffer &&
                overlaps(left, right, preferences.travel_buffer_minutes)) {
                attention.push({
                    key: `turnaround:${left.id}:${right.id}`,
                    kind: 'tight_turnaround',
                    severity: 'info',
                    title: 'Travel buffer is too short',
                    detail: `${shared.map((id) => staffById.get(id)?.name ?? 'A worker').join(', ')} has less than ${preferences.travel_buffer_minutes} minutes between ${left.job_number} and ${right.job_number}.`,
                    appointment_ids: [left.id, right.id],
                    staff_ids: shared,
                });
            }
        }
    }
    const workdayStart = timeMinutes(preferences.workday_start);
    const workdayEnd = timeMinutes(preferences.workday_end);
    for (const appointment of active) {
        const needed = requiredSkills[appointment.id] ?? [];
        if (needed.length && appointment.staff.length) {
            const available = unique(appointment.staff.flatMap((item) => (staffById.get(item.id)?.skills ?? []).map(skillKey)));
            const missing = needed.filter((skill) => !available.includes(skill));
            if (missing.length) {
                attention.push({
                    key: `skills:${appointment.id}`,
                    kind: 'skill_mismatch',
                    severity: severityFor(appointment),
                    title: `${appointment.job_number} needs another skill`,
                    detail: `Missing: ${missing.join(', ')}.`,
                    appointment_ids: [appointment.id],
                    staff_ids: appointment.staff.map((item) => item.id),
                });
            }
        }
        const expected = expectedMinutes.get(appointment.id);
        if (expected && minutesBetween(appointment.starts_at, appointment.ends_at) < expected) {
            attention.push({
                key: `duration:${appointment.id}`,
                kind: 'duration_shortfall',
                severity: 'info',
                title: `${appointment.job_number} may need more time`,
                detail: `${minutesBetween(appointment.starts_at, appointment.ends_at)} minutes scheduled; configured guidance is ${expected}.`,
                appointment_ids: [appointment.id],
                staff_ids: appointment.staff.map((item) => item.id),
            });
        }
        if (workdayStart !== null && workdayEnd !== null) {
            const start = localParts(appointment.starts_at, raw.timezone);
            const end = localParts(appointment.ends_at, raw.timezone);
            if (start.date !== end.date || start.minutes < workdayStart || end.minutes > workdayEnd) {
                attention.push({
                    key: `workday:${appointment.id}`,
                    kind: 'outside_workday',
                    severity: 'info',
                    title: `${appointment.job_number} is outside working hours`,
                    detail: `Configured hours are ${preferences.workday_start}–${preferences.workday_end}.`,
                    appointment_ids: [appointment.id],
                    staff_ids: appointment.staff.map((item) => item.id),
                });
            }
        }
    }
    const days = Math.max(1, Math.ceil((new Date(raw.to).getTime() - new Date(raw.from).getTime()) / 86_400_000));
    const dailyCapacity = workdayStart !== null && workdayEnd !== null ? Math.max(0, workdayEnd - workdayStart) : null;
    const teamLoad = raw.staff.map((staff) => ({
        staff_id: staff.id,
        staff_name: staff.name,
        color: staff.color,
        scheduled_minutes: workload.get(staff.id) ?? 0,
        appointment_count: active.filter((appointment) => appointment.staff.some((item) => item.id === staff.id)).length,
        capacity_minutes: dailyCapacity === null ? null : dailyCapacity * days,
    }));
    attention.sort((left, right) => {
        const rank = { urgent: 0, warning: 1, info: 2 };
        return rank[left.severity] - rank[right.severity] || left.title.localeCompare(right.title);
    });
    reveal.attention = attention.length > 0;
    return {
        ...raw,
        effective_preferences: preferences,
        reveal,
        attention,
        recommendations,
        team_load: teamLoad,
        required_skills_by_appointment: requiredSkills,
    };
}
export const DISPATCH_GUIDE = `Progressive dispatch keeps the default calendar simple. Advanced signals appear only when real usage or tenant configuration makes them relevant: multiple staff reveals team load, service requirements reveal skill and duration guidance, working hours reveal capacity exceptions, and a travel buffer reveals tight turnarounds. Suggestions never activate configuration or change assignments by themselves.`;
//# sourceMappingURL=dispatch.js.map